import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { FunctionComponent } from 'react';

import { REPORTS_V2 } from '../../../../../../shared/featureFlags/flagNames';
import { window } from '../../../../../globals';
import { useIsFeatureEnabled } from '../../../../../helpers/features';
import projectGroupSelectors, { getSelectedReleaseId } from '../../../../../redux/project_grouping/selectors';
import * as ReportGenerationStore from '../../../../../redux/reportGeneration';
import Loader from '../../../../generic/Loader';
import {
  defaultFixPlanOptions,
  defaultIncludeOptions,
  ntiaflatIncludeValues,
  ntiaLicenseValues,
  ntiaMetadataCheckboxValues,
} from '../shared/constants';
import { DefaultDependencyInfoOptions } from '../utils';
import type { ReportExclusionOptions, ReportGenerationParams } from '../../shared/types';
import type { FormatName } from '../shared/types';

import { Provider } from './ReportContainerContext';

type Props = {
  params: ReportGenerationParams;
};

const ProjectGroupReportContainer: FunctionComponent<Props> = ({ params, children }) => {
  const dispatch = useDispatch();
  const isReportsV2Enabled = useIsFeatureEnabled(REPORTS_V2);

  const groupId = parseInt(params.projectGroupId || '', 10);
  const releaseId: number | undefined = useSelector(getSelectedReleaseId);
  const currentProjectGroup = useSelector((state) => projectGroupSelectors.getCurrentProjectGroup(state, { params }));

  // Initialize all the state variables that were moved from ReportGenerationV2
  const [dependencyInfoOptions, setDependencyInfoOptions] = useState(DefaultDependencyInfoOptions);
  const [format, setFormat] = useState<FormatName | null>(null);
  const [includeOptions, setIncludeOptions] = useState([
    ...defaultIncludeOptions,
    ...ntiaflatIncludeValues,
    ...ntiaLicenseValues,
  ]);
  const [dependencyMetadataOptions, setDependencyMetadataOptions] = useState([
    ...DefaultDependencyInfoOptions,
    ...ntiaMetadataCheckboxValues,
  ]);
  const [excludeOptions, setExcludeOptions] = useState<ReportExclusionOptions>({});
  const [fixPlanOptions, setFixPlanOptions] = useState([...defaultFixPlanOptions]);
  const [authorName, setAuthorName] = useState(window.user?.organization?.sbomReportAuthorName || '');
  const [authorEmail, setAuthorEmail] = useState(
    window.user?.organization?.sbomReportAuthorEmail || window.user?.email || '',
  );
  const [supplier, setSupplier] = useState(
    window.user?.organization?.sbomReportSupplier || window.user?.organization?.title || '',
  );
  const [useReportsV2Toggle, setUseReportsV2Toggle] = useState(isReportsV2Enabled);

  useEffect(() => {
    if (currentProjectGroup) {
      dispatch(ReportGenerationStore.actions.setCustomHeaderText(currentProjectGroup.reportCustomText));
    }
    if (groupId && releaseId) {
      dispatch(
        ReportGenerationStore.actions.setProjectGroupContext({ projectType: 'releaseGroup', groupId, releaseId }),
      );
    }
  }, [dispatch, groupId, releaseId, currentProjectGroup]);

  if (!groupId && !releaseId) {
    return <Loader />;
  }

  return (
    <Provider
      value={{
        dependencyInfoOptions,
        setDependencyInfoOptions,
        format,
        setFormat,
        includeOptions,
        setIncludeOptions,
        dependencyMetadataOptions,
        setDependencyMetadataOptions,
        excludeOptions,
        setExcludeOptions,
        fixPlanOptions,
        setFixPlanOptions,
        authorName,
        setAuthorName,
        authorEmail,
        setAuthorEmail,
        supplier,
        setSupplier,
        useReportsV2Toggle,
        setUseReportsV2Toggle,
      }}
    >
      {children}
    </Provider>
  );
};

export default ProjectGroupReportContainer;
